import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { formatSelectPlaceholder } from "../select/utilities/use-select-preview";
import { formatVariableLabel, type IVariableToken } from "../variable-token";
import type { IVariableMentionAttributes, VariableMentionPillProps } from "./variable-mention.types";

/**
 * Flattens a variable into the ProseMirror attributes of a `variableMention` node. ProseMirror attributes
 * always carry a value, so an absent subtype becomes `null`. The arrays are copied so the node never shares them.
 * @param variable The variable to store on the node.
 * @returns The node's attributes.
 */
export const variableTokenToAttributes = (variable: IVariableToken): IVariableMentionAttributes => ({
    type: variable.type,
    subtype: variable.subtype ?? null,
    values: Object.fromEntries(Object.entries(variable.values).map(([key, list]) => [key, [...list]])),
    title: variable.title,
    labels: [...variable.labels],
});

/**
 * The inverse of `variableTokenToAttributes`. A `null` subtype is dropped, because `serialiseVariableToken` would
 * treat it as present and reject it.
 * @param attributes The `variableMention` node's attributes.
 * @returns The variable the node stands for.
 */
export const variableTokenFromAttributes = ({
    type,
    subtype,
    values,
    title,
    labels,
}: IVariableMentionAttributes): IVariableToken =>
    subtype === null ? { type, values, title, labels } : { type, subtype, values, title, labels };

/**
 * Reads the variable a `variableMention` node stands for. ProseMirror types every attribute as `any`, so the
 * fields are picked off one by one into the typed shape rather than the whole record being cast.
 * @param node The `variableMention` node.
 * @returns The variable the node stands for.
 */
export const variableTokenFromNode = (node: ProseMirrorNode): IVariableToken => {
    const { type, subtype, values, title, labels } = node.attrs;

    return variableTokenFromAttributes({ type, subtype, values, title, labels });
};

/**
 * Whether a variable is empty: a pill the user hasn't picked a value for yet. It has no labels, and keeps its `type`,
 * `subtype`, `title` and any values that say which variable it is (e.g. a tag pill's `tagGroupId`). An empty variable
 * lives in the editor only: it is drawn as a placeholder pill and never serialised, so it never reaches a sent message.
 * The token grammar has no empty form.
 * @param variable The variable, or anything carrying its labels.
 * @returns Whether it has no labels.
 */
export const isEmptyVariable = ({ labels }: Pick<IVariableToken, "labels">): boolean => labels.length === 0;

/**
 * What a pill draws when the consumer gives no `getPillProps`, knowing nothing of any variable kind: the joined labels,
 * with the variable's title as the tooltip and one tooltip line per value when there are several. An empty pill reads
 * as an empty select of its lower-cased title and has no tooltip. Neither has an icon.
 * @param variable The variable the pill stands for.
 * @returns The pill's label, tooltip and empty state.
 */
export const getDefaultVariablePillProps = (variable: IVariableToken): VariableMentionPillProps => {
    if (isEmptyVariable(variable)) {
        return { label: formatSelectPlaceholder(variable.title.toLocaleLowerCase()), empty: true };
    }

    return {
        label: formatVariableLabel(variable),
        tooltip: {
            title: variable.title,
            ...(variable.labels.length > 1 ? { items: variable.labels } : {}),
        },
        empty: false,
    };
};
